import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { FormBuilder, Validators } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import {
  amountMask,
  ApiService,
  AppointmentModel,
  Card,
  Clinic,
  ClinicService,
  ConfirmService,
  Invoice,
  InvoiceModel,
  InvoicePlan,
  Patient,
  PatientDataService,
  PatientPalRestError,
  PAYMENT_PROVIDER,
  ProgressIndicatorService,
  TransactionMethod,
} from '@mmx/shared'
import { first, range } from 'lodash'
import moment from 'moment'
import { Subject } from 'rxjs'
import { takeUntil, tap } from 'rxjs/operators'

import { PatientCreditCardComponent } from '../patient-credit-card'

@Component({
  selector: 'mmx-payment-plan-form',
  templateUrl: './payment-plan-form.component.html',
  styleUrls: [
    '../charge-card/charge-card.component.scss',
    './payment-plan-form.component.scss',
  ],
  standalone: false,
})
export class PaymentPlanFormComponent implements OnInit, OnDestroy {
  private clinic: Clinic

  @Output() newCharge = new EventEmitter<any>()
  @Input() patient: Patient
  @Input() appointment: AppointmentModel
  @Input() chargeAmount: number
  @Input() invoice: InvoiceModel

  get card(): Card | undefined {
    return this.creditCards.find(card => card.id === this.paymentPlanForm.value.creditCard)
  }

  paymentPlanForm = this.fb.group({
    creditCard: this.fb.control<string>(null, Validators.required),
    months: this.fb.control<number>(2, Validators.required),
    minPayment: this.fb.control<number>(null, Validators.required),
    interest: this.fb.control<number>(0, Validators.required),
    installmentFee: this.fb.control<number>(0, Validators.required),
  })

  amountMask = amountMask()
  serverResponse: string
  responseColor: string
  isSaving = false
  months = range(2, 37)
  feePercentage: number
  feeAmount: number
  isDiscount = false
  invoicePlan: InvoicePlan = { }
  hold = false

  private onDestroy$ = new Subject<void>()

  get total() {
    return this.chargeAmount
  }

  creditCards: Card[] = []

  cards$ = this.patientData.cards$.pipe(
    takeUntil(this.onDestroy$),
    tap(cards => {
      this.creditCards = cards

      const selected = this.paymentPlanForm.value.creditCard

      // A deleted card leaves a dead id here: required still passes, the select renders blank, and
      // staff re-post the same card so the attempt counter never clears.
      if (cards?.length > 0 && !cards.some(card => card.id === selected)) {
        this.paymentPlanForm.patchValue({
          creditCard: first(cards).id,
        })

        this.paymentPlanForm.updateValueAndValidity()
      }
    }),
  )

  constructor(
    private dialog: MatDialog,
    private progressService: ProgressIndicatorService,
    private clinicService: ClinicService,
    private restService: ApiService,
    private patientData: PatientDataService,
    private confirmService: ConfirmService,
    private fb: FormBuilder,
  ) {}

  ngOnInit() {
    this.clinic = this.clinicService.clinic

    if (this.clinic.settings.defaultCurrency) {
      this.amountMask = amountMask(this.clinic.settings.defaultCurrency)
    }
    if (this.clinic.modulesPreferences.maxLoanTerm) {
      this.months.splice(this.clinic.modulesPreferences.maxLoanTerm - 1, 0, null)
    }

    if (this.invoice.plan) {
      // An agreed plan keeps its own terms; the clinic's current rates describe new plans only.
      this.feePercentage = this.invoice.plan.feePercentage ?? 0
      this.feeAmount = this.invoice.plan.feeAmount ?? 0
    } else {
      this.feePercentage = this.clinic.modulesPreferences.feePercentage ?? 0
      this.feeAmount = this.clinic.modulesPreferences.feeAmount ?? 0
    }

    this.paymentPlanForm.patchValue({
      creditCard: this.invoice.plan?.paymentMethodId,
      months: this.invoice.plan?.months ?? 2,
      minPayment: this.clinic.modulesPreferences.minPayment ? parseFloat(this.clinic.modulesPreferences.minPayment) : 0,
      interest: this.feePercentage,
      installmentFee: this.feeAmount,
    })

    if (this.invoice.plan) {
      this.disablePlanTermControls()
    }

    this.paymentPlanForm.controls.interest.valueChanges.subscribe((interest) => {
      this.feePercentage = interest
    })

    this.paymentPlanForm.controls.installmentFee.valueChanges.subscribe((installmentFee) => {
      this.feeAmount = installmentFee
    })

    this.paymentPlanForm.valueChanges.subscribe(() => this.calculatePaymentPlan())

    this.cards$.subscribe()
  }

  ngOnDestroy() {
    this.onDestroy$.next()
  }

  /**
   * Open create card dialog
   */
  public openAddCardDialog(): void {
    const diaRef = this.dialog.open(PatientCreditCardComponent, {
      panelClass: 'dialog-form',
      width: '500px',
      data: {
        patient: this.patient,
      },
    })

    diaRef.afterClosed().subscribe((refresh) => {
      if (refresh) {
        this.patientData.refreshCards$.next()
      }
    })
  }

  public submitPaymentPlan() {
    if (this.paymentPlanForm.invalid) {
      return
    }

    this.isSaving = true
    const provider = this.card == null
      ? undefined
      : this.card.provider === 'stripe'
        ? PAYMENT_PROVIDER.STRIPE
        : PAYMENT_PROVIDER.TILLED

    if (this.invoice.plan) {
      // Update payment card
      this.invoice.plan.paymentMethodId = this.paymentPlanForm.value.creditCard
      const data = this.invoice.toJSON()

      if (provider) {
        data.provider = provider
      }

      this.restService.put<any>(`/invoices/${this.invoice.id}`, data).subscribe(
        () => {
          this.isSaving = false
          this.progressService.snack('Updated card in Payment Plan')
        },
        () => {
          this.isSaving = false
          this.progressService.snack('Error updating card in Payment Plan')
        })
    } else {
      // Create Payment plan
      const invoiceData = this.invoice.toJSON()

      invoiceData.paymentOption = Invoice.PAYMENT_OPTION.PLAN
      invoiceData.feeDue += this.invoicePlan.fee
      invoiceData.feeRemaining += this.invoicePlan.fee
      invoiceData.total += this.invoicePlan.fee
      invoiceData.amountDue = invoiceData.total
      invoiceData.amountRemaining += this.invoicePlan.fee
      invoiceData.plan = this.invoicePlan
      invoiceData.provider = provider
      invoiceData.status = Invoice.STATUS.PAYING

      // Save payment plan
      this.restService.put<any>(`/invoices/${this.invoice.id}`, invoiceData).subscribe(
        (invoice) => {
          const fee = invoiceData.feeRemaining ? Number((invoiceData.feeRemaining / invoiceData.plan.months).toFixed(2)) : 0
          const pay: any = {
            amount: invoice.data.plan.firstPayment,
            fee,
            paymentMethodId: this.card?.id,
            paymentMethodTypes: ['card'],
            method: TransactionMethod.STAFF_CREDIT_CARD,
            provider: this.card?.provider ?? PAYMENT_PROVIDER.STRIPE,
          }

          this.restService.submitInvoicePayment(this.invoice.id, pay).subscribe(
            (res) => {
              this.isSaving = false
              this.invoice = new InvoiceModel(invoice.data)
              // Emit new transaction
              this.newCharge.emit(res.data)
            },
            (err: PatientPalRestError) => {
              this.isSaving = false
              const errorMessage = err.error?.message || 'Unable to process payment. Please try again.'
              this.progressService.snack(errorMessage)
            },
          )
        },
        () => {
          this.isSaving = false
          this.progressService.snack('Error updating invoice with payment plan')
        })
    }
  }

  /** On an existing plan the term fields are read-only, so they must not gate a card change. */
  private disablePlanTermControls() {
    const { months, minPayment, interest, installmentFee } = this.paymentPlanForm.controls

    for (const control of [months, minPayment, interest, installmentFee]) {
      control.disable({ emitEvent: false })
    }
  }

  public getErrorMessage(controlName: string, actualName: string): string {
    const control = this.paymentPlanForm.get(controlName)

    if (control) {
      if (control.hasError('required')) {
        return `${actualName} is required`
      } else if (control.hasError('minlength') || control.hasError('maxlength')) {
        return `${actualName} is an invalid length`
      } else if (control.hasError('min')) {
        const error = control.getError('min')
        return `${actualName} must be at least ${error.min}`
      } else if (control.hasError('pattern')) {
        return `${actualName} must be a number`
      }
    }

    return ''
  }

  public safeCurrency(amount: number | string): number | undefined {
    if (typeof amount === 'number') {
      return amount
    } else if (typeof amount === 'string') {
      return parseFloat(amount.replace(/[^0-9.-]+/g, ''))
    }
  }

  public calculatePaymentPlan() {
    const { months, interest, installmentFee } = this.paymentPlanForm.getRawValue()
    this.invoicePlan.months = months
    this.invoicePlan.startAt = moment().toDate()
    this.invoicePlan.nextPaymentAt = moment().add(1, 'month').toDate()
    this.invoicePlan.endAt = moment().add(months - 1, 'months').toDate()
    this.invoicePlan.subtotal = this.invoice.amountRemaining
    this.invoicePlan.fee = (this.invoicePlan.subtotal * interest / 100) + installmentFee * months
    this.invoicePlan.feePercentage = interest
    this.invoicePlan.feeAmount = installmentFee
    this.invoicePlan.total = this.invoicePlan.subtotal + this.invoicePlan.fee

    this.calculatePaymentPlanMonthlyAmount(months)
  }

  public cancelPaymentPlan() {
    const message = `Would you like to cancel payment plan?`

    this.confirmService.confirm(message)
      .subscribe((isConfirmed) => {
        if (isConfirmed) {
          const invoiceData = this.invoice.toJSON()
          invoiceData.provider = invoiceData.paymentProvider
          // Remove remaining fees
          invoiceData.total = (invoiceData.total - invoiceData.feeRemaining)
          invoiceData.amountDue = invoiceData.total
          invoiceData.amountRemaining = invoiceData.amountRemaining - invoiceData.feeRemaining
          invoiceData.feeDue = invoiceData.feePaid
          invoiceData.feeRemaining = 0

          // Reset plan and taxes
          invoiceData.paymentOption = ''
          invoiceData.plan = { }

          if (invoiceData.amountRemaining === 0) {
            invoiceData.status = Invoice.STATUS.PAID
          }

          this.restService.put<any>(`/invoices/${this.invoice.id}`, invoiceData).subscribe(
            (invoice) => {
              this.isSaving = false
              this.invoice = new InvoiceModel(invoice.data)
              this.newCharge.emit({ invoice: invoice.data })

              // Update payment plan
              this.calculatePaymentPlan()
            },
            () => {
              this.isSaving = false
              this.progressService.snack('Error cancelling payment plan')
            })
        }
      })
  }

  private calculatePaymentPlanMonthlyAmount(months: number) {
    const minPayment = String(this.paymentPlanForm.getRawValue().minPayment)
    const maxLoanAmount = this.clinic.modulesPreferences.maxLoanAmount ? this.clinic.modulesPreferences.maxLoanAmount : 0
    this.invoicePlan.months = months

    // Calculate minimum first payment if it was setup
    this.invoicePlan.firstPayment = this.invoicePlan.total / this.invoicePlan.months

    if (minPayment?.length > 0 && minPayment !== '$') {
      if (minPayment.includes('%')) {
        this.invoicePlan.firstPayment = parseFloat(minPayment.replace('%', '')) * this.invoice.amountRemaining / 100
      } else {
        this.invoicePlan.firstPayment = parseFloat(minPayment.replace('$', ''))
      }

      if (maxLoanAmount && maxLoanAmount <= this.invoicePlan.total && maxLoanAmount > this.invoicePlan.firstPayment) {
        const firstPayment = this.invoicePlan.total - maxLoanAmount

        if (this.invoicePlan.firstPayment > this.invoicePlan.firstPayment) {
          this.invoicePlan.firstPayment = firstPayment
        }
      }
    }

    this.invoicePlan.monthlyAmount = this.invoicePlan.total / this.invoicePlan.months

    if (this.invoicePlan.firstPayment < this.invoicePlan.monthlyAmount) {
      // Reset if minPayment calculate previously is lower than monthly amount
      this.invoicePlan.firstPayment = this.invoicePlan.monthlyAmount
    } else {
      const total = this.invoicePlan.total - this.invoicePlan.firstPayment
      this.invoicePlan.monthlyAmount = total / (this.invoicePlan.months - 1) // First payment is part of the payment plan
    }

    // Round monthly amount two decimal
    this.invoicePlan.firstPayment = parseFloat(this.invoicePlan.firstPayment.toFixed(2))
    this.invoicePlan.monthlyAmount = parseFloat(this.invoicePlan.monthlyAmount.toFixed(2))
  }
}
